/**
 * The READ CONTRACT for one role's coverage — the shape that crosses HTTP.
 *
 * `coverage-ratio.ts` produces a {@link RoleCoverage}, which is a domain value:
 * branded formulas, `readonly` arrays, types the wire has never heard of. This
 * file is the serialisable projection of it, plus the two things the authoring
 * surface needs that the ratio has no reason to know about — WHICH ROLE, and
 * WHICH ASSUMPTIONS THE FIGURE RESTS ON.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE ASSUMPTIONS SHIP WITH THE NUMBER. THIS IS NOT OPTIONAL.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The working day, the working week and the working year are per-organisation
 * inputs a customer chose, and a role may override any of them. They therefore
 * belong ON the surface where the numbers are authored and read — not buried in
 * an organisation settings panel two clicks away, and not fetched by a second
 * request the caller may forget to make.
 *
 * So {@link RoleCoverageViewSchema} carries all three EFFECTIVE values AND the
 * ORIGIN of each, in one response. A client renders "46 weeks/year (this role)"
 * beside "8 h/day (organisation default)" from what it already has.
 *
 * The pairs come from `coverage-working-time.resolver.ts`, which is the only
 * thing that can produce them and is also the only thing that feeds the
 * evaluator — so the assumptions this response reports are, structurally, the
 * assumptions the percentage was computed from.
 *
 * ── THIS IS THE COVERAGE READ, NOT THE AUTHORING READ ───────────────────────
 *
 * `workload` carries the authored DENOMINATOR, because the figure is not
 * checkable without it: a reader shown "18.29%" needs "12 people × 35 h/week ×
 * 46 weeks" under it or the number is an assertion. It is `nullable`, and a
 * `null` never means "the response could not represent it" — a model that
 * failed to parse is absent HERE and present in `integrity.warnings`, named,
 * with its reason.
 *
 * Validating a stored model inside the RESPONSE schema would otherwise let one
 * unreadable formula fail the parse of the whole payload and blank a page — the
 * exact "invalid model disappears, the aggregate still renders confidently"
 * failure `coverage-integrity.types.ts` exists to prevent, arriving one layer
 * out. A model this response carries has already passed `CoverageFormulaSchema`
 * on the server, so re-validating it here cannot fire on data the server
 * produced and cannot blank the read on data it did not.
 *
 * What this contract deliberately does NOT carry is any RAW stored JSON.
 * REPAIRING a broken model needs the raw row, and that belongs to the authoring
 * surface's own read (`role-coverage-authoring-view.schema.ts`), where
 * "unparseable" is the subject rather than an obstacle. A model that failed to
 * parse is `null` here — on `workload` and on a contribution alike — and named
 * in `integrity.warnings`.
 *
 * ── A CONTRIBUTION CARRIES ITS VALIDATED HOURS MODEL, FOR `workload`'s REASON ─
 *
 * This paragraph used to refuse the per-system models outright, on the ground
 * that "a contribution names its system by `resourceType` / `resourceId`, which
 * is all a reader of a percentage needs". That is the argument this same header
 * already overrules two paragraphs up for the denominator: a reader shown
 * "18.29%" needs "12 people × 35 h/week × 46 weeks" under it or the number is an
 * assertion. A system's `personHours` is the same claim one level down, and it
 * had no operands under it.
 *
 * The consequence was not only cosmetic. The impact form is a whole-row `PUT`
 * with three boxes, and with nothing to rehydrate them from it opened EMPTY over
 * a stored model every time — so a reader who came to adjust a cost retyped the
 * hours from memory or silently replaced them. `formula` is what closes that,
 * and it is the VALIDATED model rather than the raw row, which is why it does
 * not reopen the repair question the authoring read owns.
 */
import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { COVERAGE_BASES } from "./coverage-basis";
import {
  COVERAGE_EVALUATION_FAILURE_CODES,
  COVERAGE_MONEY_NOT_MODELLED_REASONS,
  COVERAGE_NOT_MODELLED_REASONS,
  COVERAGE_SAVINGS_PROJECTION_UNAVAILABLE_REASONS,
  COVERAGE_WARNING_CODES,
  COVERAGE_WARNING_SEVERITIES
} from "./coverage-enumerations";
import { COVERAGE_FUNCTIONS, CoverageFormulaSchema } from "./coverage-formula.schema";
import { COVERAGE_MONEY_TERMS, CoverageCurrencyCodeSchema } from "./coverage-money.types";
import { COVERAGE_WORKING_TIME_ORIGINS } from "./coverage-working-time.resolver";

/**
 * WHICH BUCKET A SYSTEM'S FIGURES LAND IN — `BUILDING | LIVE | RETIRED`.
 *
 * `z.enum(DbEnum.X_VALUES)`, never `z.nativeEnum(DbEnum.X)`: this value is
 * assigned FROM a Prisma row, and Prisma hands a read row the string-literal
 * union rather than the TypeScript enum type. `role-scope-authoring.schema.ts`
 * carries the full argument and the error a mapper gets from the wrong one.
 *
 * ── WHY IT IS ON THIS CONTRACT AT ALL ───────────────────────────────────────
 *
 * Only LIVE systems are summed into `impactPersonHours` and into
 * `money.totals`, so without this field a reader could not tell a Role whose
 * systems are all running from one whose systems are half built — the two
 * publish the same headline and a different set of rows under it. It is
 * published per row AND aggregated in `impactPersonHoursByLifecycle`, because
 * naming the bucket and sizing it are different questions.
 *
 * 🚨 IT SAYS NOTHING ABOUT `basis`. A live hand-estimated system is an
 * estimate; a system still being built whose volumes were measured is measured.
 * `coverage-basis.ts` owns that rule and
 * `coverage-lifecycle-basis.test.ts` pins it.
 */
export const RoleResourceLifecycleSchema = z.enum(DbEnum.ROLE_RESOURCE_LIFECYCLE_VALUES);

/** Where a failure happened, precise enough to highlight in the authoring UI. */
export const CoverageEvaluationSiteSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("input"), key: z.string() }),
  z.strictObject({
    kind: z.literal("step"),
    index: z.number().int().min(0),
    fn: z.enum(COVERAGE_FUNCTIONS)
  }),
  z.strictObject({ kind: z.literal("result") })
]);

/** A typed evaluation failure, as it crosses the wire. */
export const CoverageEvaluationFailureSchema = z.strictObject({
  code: z.enum(COVERAGE_EVALUATION_FAILURE_CODES),
  message: z.string(),
  at: CoverageEvaluationSiteSchema
});

/** What one warning is about. */
export const CoverageWarningSubjectSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("workingTime") }),
  z.strictObject({ kind: z.literal("workload") }),
  z.strictObject({ kind: z.literal("impact"), id: z.string() }),
  z.strictObject({ kind: z.literal("ratio") }),
  z.strictObject({ kind: z.literal("workloadMoney"), term: z.enum(COVERAGE_MONEY_TERMS) }),
  z.strictObject({
    kind: z.literal("impactMoney"),
    id: z.string(),
    term: z.enum(COVERAGE_MONEY_TERMS)
  })
]);

/** One named problem with a coverage figure. */
export const CoverageWarningSchema = z.strictObject({
  code: z.enum(COVERAGE_WARNING_CODES),
  severity: z.enum(COVERAGE_WARNING_SEVERITIES),
  subject: CoverageWarningSubjectSchema,
  message: z.string()
});

/** Whether a coverage figure can be trusted, and why not. */
export const CoverageIntegritySchema = z.strictObject({
  status: z.enum(["OK", "DEGRADED"]),
  warnings: z.array(CoverageWarningSchema).readonly()
});

/**
 * A coverage figure, or an explicit statement that there is none.
 *
 * A discriminated union on the wire exactly as it is in the domain, so the
 * "no percentage" state survives serialisation. Flattening it to a nullable
 * number here would re-introduce the sentinel the whole design refuses — see
 * the null guardrail in `coverage-ratio.ts`.
 *
 * `ratio` is a fraction, never a percentage: `0.1828`, not `18.28`.
 */
export const CoverageRatioSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("not-modelled"),
    reason: z.enum(COVERAGE_NOT_MODELLED_REASONS)
  }),
  z.strictObject({ kind: z.literal("modelled"), ratio: z.number() })
]);

/**
 * One money term of one system, as it crosses the wire.
 *
 * Three arms, not a nullable number, for the reason {@link CoverageRatioSchema}
 * is a union: "declares no revenue model", "declares one that is broken" and
 * "declares one worth exactly zero" are three different facts, and a `null`
 * would flatten the first two into each other and into the third's neighbour.
 */
export const CoverageMoneyTermSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("absent") }),
  z.strictObject({ kind: z.literal("amount"), amount: z.number() }),
  z.strictObject({
    kind: z.literal("failed"),
    /** `null` when the model evaluated cleanly to the wrong dimension or window. */
    failure: CoverageEvaluationFailureSchema.nullable()
  })
]);

/**
 * The money side of one role's coverage.
 *
 * ONE CURRENCY FOR THE WHOLE OBJECT, and it is inside the `modelled` arm rather
 * than beside it — so an amount cannot be read without the currency it is
 * stated in, and "the organisation never stated a currency" cannot be rendered
 * as an unlabelled number. There is no per-figure currency because the engine
 * never converts: one organisation, one currency, every model already stated in
 * it.
 *
 * `net` is the only derived figure here, it has no reader yet, and it does NOT
 * ship computed for the float reason this comment used to give — `revenue` and
 * `cost` cross the wire as finished totals, so a caller's subtraction is bit
 * identical. `coverage-money.types.ts` carries the correction, why a role-wide
 * revenue total and a role-wide net cost are deliberately absent, and the
 * condition under which `net` follows them.
 */
export const RoleCoverageMoneySchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("not-modelled"),
    reason: z.enum(COVERAGE_MONEY_NOT_MODELLED_REASONS)
  }),
  z.strictObject({
    kind: z.literal("modelled"),
    currency: CoverageCurrencyCodeSchema,
    totals: z.strictObject({
      /** The role's own annual cost — salaries, seats. `null` when nobody modelled it. */
      workloadCost: z.number().nullable(),
      /**
       * Revenue the role holds that is attached to no system. `null` when
       * nobody modelled it — never a zero, which would be a claim.
       */
      workloadRevenue: z.number().nullable(),
      /** Revenue the systems generate. */
      revenue: z.number(),
      /** What the systems cost. */
      cost: z.number(),
      /**
       * `revenue - cost` — the automation's own P&L, blind to salaries.
       * Never render it as the role's net cost: it omits `workloadCost`.
       */
      net: z.number()
    })
  })
]);

/**
 * Saved work re-expressed in money, as it crosses the wire.
 *
 * A union rather than a nullable number, exactly as its siblings are, and the
 * `projected` arm carries BOTH the currency and the rate it was produced with.
 * The rate is not decoration: it is what stops a reader taking this for a
 * measured amount, and `coverage-savings-projection.types.ts` carries the
 * argument for publishing it.
 */
export const CoverageSavingsProjectionSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("unavailable"),
    reason: z.enum(COVERAGE_SAVINGS_PROJECTION_UNAVAILABLE_REASONS)
  }),
  z.strictObject({
    kind: z.literal("projected"),
    amount: z.number(),
    currency: CoverageCurrencyCodeSchema,
    /** The Role's labour cost divided by its worked hours, per person-hour. */
    ratePerHour: z.number()
  })
]);

/** One effective working-time assumption, and who chose it. */
export const ResolvedCoverageWorkingTimeSettingSchema = z.strictObject({
  value: z.number(),
  origin: z.enum(COVERAGE_WORKING_TIME_ORIGINS)
});

/**
 * The three assumptions the figure rests on, each with its origin.
 *
 * Per FIELD rather than one origin for the group, because the override is per
 * field: a role on a four-day week keeps the organisation's working year, and a
 * single group-level origin could describe neither half honestly.
 */
export const ResolvedCoverageWorkingTimeSchema = z.strictObject({
  hoursPerDay: ResolvedCoverageWorkingTimeSettingSchema,
  daysPerWeek: ResolvedCoverageWorkingTimeSettingSchema,
  workingWeeksPerYear: ResolvedCoverageWorkingTimeSettingSchema
});

/**
 * Why a system produced no contribution — a NAMED absence, never a figure.
 *
 * 🚨 THIS SHAPE CARRIES NO NUMBER, AND THAT IS THE WHOLE DESIGN. There is
 * deliberately no `personHours: 0` here and no nullable one either. A zero for a
 * system nobody has measured is a fabricated measurement, and a `null` beside
 * the real `personHours: null` of a model that failed to EVALUATE would make two
 * different states render identically. The type is what makes the third state
 * unrepresentable as the other two, rather than a convention someone has to keep.
 *
 * `coverage-ratio.test.ts` already pins "not-modelled, 0% and 100% are three
 * distinguishable states" for the RATIO. This is the same rule one level down,
 * at the system.
 */
export const UNMODELLED_ROLE_SYSTEM_REASONS = ["NO_IMPACT_MODEL"] as const;

export const UnmodelledRoleSystemReasonSchema = z.enum(UNMODELLED_ROLE_SYSTEM_REASONS);

/**
 * A `RoleResource` the Role holds that has no impact model at all.
 *
 * `id` is the `RoleResource` row id, the same key `RoleCoverageContributionView`
 * and every warning's `subject.id` use — so a client joins the two lists with no
 * lookup table, and a `RoleResource` appears in exactly one of them.
 *
 * 🚨 THAT PARTITION IS OVER `RoleResource`, NEVER OVER EVERYTHING THE ROLE
 * HOLDS. A Collection or a Workspace reaches a Role by GRANT, and
 * `RoleSystemImpact.roleResourceId` is a `@unique` foreign key onto
 * `RoleResource` — so a grant can carry no impact model and appears in neither
 * list. `RoleCoverageViewSchema`'s `grantedSystems` counts them.
 *
 * This docblock said "a system" until 2026-08-12, which read as the wider claim
 * and was false for any Role holding a grant.
 */
export const UnmodelledRoleSystemViewSchema = z.strictObject({
  id: z.string(),
  resourceType: z.string(),
  resourceId: z.string(),
  /**
   * The bucket this system is in, even though it carries no figure.
   *
   * A system can be BUILDING and unmodelled at once — that is the ordinary
   * state of something somebody just started — and without this a client
   * rendering "3 systems being built" from `contributions` alone undercounts by
   * exactly the ones nobody has priced yet.
   */
  lifecycle: RoleResourceLifecycleSchema,
  reason: UnmodelledRoleSystemReasonSchema
});

/**
 * How many systems this Role holds by GRANT, per kind.
 *
 * A Role holds systems through FOUR tables. `RoleResource` is exclusive
 * ownership and is the population `contributions` and `unmodelledSystems`
 * partition. `RoleCollectionGrant`, `RoleWorkspaceGrant` and
 * `RoleExternalToolGrant` are the M:N grants a Collection, a Workspace and an
 * external Tool need because each can be shared across Roles — and no impact
 * model can point at one, so neither list can carry them.
 *
 * 🔴 `externalTools` IS THE ONE MOST LIKELY TO BE NON-ZERO AND IT IS THE
 * NEWEST, so a client written before 2026-08-13 renders a `systems` sentence
 * that silently excludes it. External tools carry 6.93% of production model
 * spend on the `externalToolId` path — more than the other two grant kinds
 * combined — so this is the count whose omission is most likely to be noticed
 * by a customer rather than by us.
 *
 * 🚨 THIS IS THE POPULATION AN AGGREGATE OVER THE OTHER TWO LISTS EXCLUDES.
 * A client rendering a count, a ratio or a "systems" sentence from
 * `contributions` and `unmodelledSystems` alone is describing part of what the
 * Role holds; these two numbers are what let it say which part. Rendering them
 * is a choice, but a surface that shows neither them nor a sentence naming what
 * it left out is showing a wrong number by omission.
 *
 * Counts, never identities — {@link RoleCoverageViewSchema}'s `grantedSystems`
 * carries the reasoning.
 */
export const GrantedRoleSystemsSchema = z.strictObject({
  /** `RoleCollectionGrant` rows for this Role. */
  collections: z.number().int().min(0),
  /** `RoleWorkspaceGrant` rows for this Role. */
  workspaces: z.number().int().min(0),
  /** `RoleExternalToolGrant` rows for this Role. */
  externalTools: z.number().int().min(0)
});

/**
 * One system's contribution to the numerator.
 *
 * `id` is the `RoleResource` row id and is what every warning's
 * `subject.id` refers to, so a client links a warning to a row without a
 * lookup table. `resourceType` and `resourceId` name the system itself —
 * the same polymorphic pair `RoleResource` carries.
 */
export const RoleCoverageContributionViewSchema = z.strictObject({
  id: z.string(),
  resourceType: z.string(),
  resourceId: z.string(),
  /**
   * WHICH BUCKET THIS ROW'S FIGURES LAND IN, and therefore whether
   * `personHours` below is inside `RoleCoverageView.impactPersonHours` or
   * outside it.
   *
   * 🚨 `contributions` IS NO LONGER A PARTITION OF THE NUMERATOR, AND A CLIENT
   * THAT SUMS `personHours` ACROSS THESE ROWS GETS A DIFFERENT NUMBER FROM THE
   * PUBLISHED `impactPersonHours`. Only `LIVE` rows are summed into the Role's
   * totals. Reading this field is what tells the two apart, and
   * `impactPersonHoursByLifecycle` is the same split already done.
   *
   * A row's own figures are published whatever the bucket — a system being
   * built shows what it WILL save, which is the number a reader came for.
   */
  lifecycle: RoleResourceLifecycleSchema,
  /** Person-hours per year, or `null` when the model did not evaluate. */
  personHours: z.number().nullable(),
  /**
   * The authored saved-work model for THIS system — the operands behind
   * `personHours`, and what an editor puts back in its boxes.
   *
   * `null` when the stored JSON did not pass `CoverageFormulaSchema`, exactly as
   * `RoleCoverageView.workload` is, with the reason in `integrity.warnings`. It
   * is never `null` for a missing model: `RoleSystemImpact.formula` is NOT NULL,
   * so a row exists only because somebody authored one.
   *
   * 🚨 REQUIRED AND NULLABLE, NEVER OPTIONAL. Every producer of this row owes a
   * value, so a future one cannot omit it and leave an editor silently reopening
   * empty over a stored model — which is the defect this field closed.
   */
  formula: CoverageFormulaSchema.nullable(),
  failure: CoverageEvaluationFailureSchema.nullable(),
  measuredInputKeys: z.array(z.string()).readonly(),
  /**
   * Whether THIS ROW's figures rest on measurements, on estimates, or on both.
   *
   * 🚨 NOT the PERIOD basis. `coverage-period.types.ts`'s `periodBasis` is the
   * exponent of the working year and is a different axis with a colliding word;
   * this one is where the magnitudes came from. `coverage-basis.ts` carries the
   * definition and the three things `MEASURED` does not claim.
   *
   * Narrower than `measuredInputKeys` beside it, deliberately: a model that
   * evaluated and was then refused appears there and not here, because it
   * reached no figure on this row.
   */
  basis: z.enum(COVERAGE_BASES),
  /** Money this system generates per year. Stated in the role's one currency. */
  revenue: CoverageMoneyTermSchema,
  /** Money this system costs per year. Stated in the role's one currency. */
  cost: CoverageMoneyTermSchema,
  /**
   * This row's `personHours` re-expressed in money — the `Impact / yr` column.
   * SAVINGS ONLY: `revenue` does not enter it.
   */
  savingsProjection: CoverageSavingsProjectionSchema
});

/** One role's coverage, and everything a reader needs to check it. */
export const RoleCoverageViewSchema = z.strictObject({
  roleId: z.string(),
  coverage: CoverageRatioSchema,
  /** The denominator in person-hours per year, or `null` when there is none. */
  workloadPersonHours: z.number().nullable(),
  /**
   * The numerator: the sum of the contributions that evaluated AND are `LIVE`.
   *
   * 🚨 THIS IS LIVE COVERAGE, NOT MODELLED COVERAGE, AND THE DIFFERENCE IS
   * CUSTOMER-VISIBLE. A system in `BUILDING` has a real, published
   * `personHours` on its own row and is deliberately absent from here: it is
   * work nobody is saving yet, and a ROI headline that counted it would report
   * a saving the customer has not received. `impactPersonHoursByLifecycle`
   * below is the same set split three ways, and its `LIVE` entry is exactly
   * this number.
   *
   * Every `RoleResource` on production is `LIVE` (the column's default IS the
   * backfill), so this field is unchanged for every figure that exists today.
   */
  impactPersonHours: z.number(),
  /**
   * The numerator, partitioned by bucket — the source for "70% live, 4% more
   * being built".
   *
   * ── AN ACCOUNTING IDENTITY, AND WHAT IT IS NOT ─────────────────────────────
   *
   * The three entries sum to the person-hours of every contribution that
   * EVALUATED, `LIVE` or not. That is NOT the sum of `contributions[].personHours`
   * — a row whose model failed to evaluate reports `personHours: null` and is in
   * neither. And it is NOT `impactPersonHours × 3` shapes of the same figure:
   * `impactPersonHours === impactPersonHoursByLifecycle.LIVE`, by construction,
   * and the other two entries are outside every other figure on this response.
   *
   * ── EXHAUSTIVE BY THE SCHEMA, NOT BY A CONVENTION ──────────────────────────
   *
   * `z.record` over an enum key refuses a partial object — measured on zod
   * 4.1.13: `{ LIVE: 2 }` fails with `invalid_type` on `BUILDING` and `RETIRED`,
   * `{ BUILDING, LIVE, RETIRED }` passes. So a fourth lifecycle value cannot be
   * added to the schema without every producer of this field failing its own
   * parse, which is the ratchet a hand-written three-key object would not have.
   *
   * Zeroes are MEASURED here, never absent: a Role with nothing being built
   * reports `BUILDING: 0`, which is a statement, where a missing key would be
   * indistinguishable from a producer that did not look.
   */
  impactPersonHoursByLifecycle: z.record(RoleResourceLifecycleSchema, z.number()),
  contributions: z.array(RoleCoverageContributionViewSchema).readonly(),
  /** Every input key a measurement replaced, across every model evaluated. */
  measuredInputKeys: z.array(z.string()).readonly(),
  /**
   * Whether this Role's published figures rest on measurements, on estimates,
   * or on both — the honesty guardrail for a screen that already renders money.
   *
   * 🚨 NOT the PERIOD basis; see the note on the contribution row.
   *
   * It describes the COVERAGE cost model — `OrganizationAutomationSettings` +
   * `RoleWorkload` + one `RoleSystemImpact` per held system — and never the JOB
   * MODEL (`RoleJobType` / `RoleScopeLine` / `RoleWorkingYear`), which shares
   * this feature's vocabulary, is evaluated in a browser, and no server-side
   * figure here comes from.
   *
   * `ESTIMATED` is also the answer when nothing was evaluated at all: it is the
   * arm that cannot overstate, and there is deliberately no fourth arm for "no
   * figure" — `coverage` and each money term already say that in their own
   * unions, and this is read beside them.
   */
  basis: z.enum(COVERAGE_BASES),
  integrity: CoverageIntegritySchema,
  /**
   * The assumptions this figure rests on, or `null` when the organisation has
   * never stated any.
   *
   * `null` IF AND ONLY IF `coverage` is
   * `{ kind: "not-modelled", reason: "NO_WORKING_TIME_MODEL" }`. A working
   * model that is stated and unusable — a zero-length day — reports
   * `WORKING_TIME_MODEL_INVALID` and DOES carry the offending values, because
   * an admin sent to fix a field needs to see the field.
   *
   * Zod cannot express that biconditional across two sibling fields without
   * collapsing the whole view into a discriminated union, which would make
   * every consumer narrow twice to read a percentage. It is pinned in both
   * directions by `get-role-coverage.use-case.spec.ts` instead.
   */
  workingTime: ResolvedCoverageWorkingTimeSchema.nullable(),
  /** The authored denominator. `null` when absent or unparseable. */
  workload: CoverageFormulaSchema.nullable(),
  /**
   * The money figures, or a named statement that there are none.
   *
   * A SECOND AGGREGATE, not a projection of `impactPersonHours` through a rate
   * — `coverage-money.types.ts` carries the argument, and the short version is
   * that a licence fee has no hours behind it and revenue is not a function of
   * saved work.
   *
   * It shares `integrity` with the hours figures rather than carrying its own.
   * That is the point: a broken cost model degrades THIS result, so it reaches
   * the organisation rollup through the same `mergeCoverageIntegrity` the hours
   * side already uses, and no consumer has to remember to merge a second
   * record. A separate money integrity is the version that renders a confident
   * ROI figure with a term silently missing.
   */
  money: RoleCoverageMoneySchema,
  /**
   * `impactPersonHours` re-expressed in money at the Role's blended rate — the
   * headline the ledger renders above the `Impact / yr` column.
   *
   * The numerator multiplied ONCE, never the sum of the rows' projections. It
   * is a projection and it is outside `money.totals` for that reason: the money
   * totals are measured amounts and this is hours wearing a currency.
   */
  savingsProjection: CoverageSavingsProjectionSchema,
  /**
   * Systems attached to this Role that nobody has modelled.
   *
   * ── WHY THIS FIELD EXISTS ───────────────────────────────────────────────────
   *
   * `contributions` is one entry per stored `RoleSystemImpact`, NOT one per
   * `RoleResource`. Before this field, a system with no impact model produced no
   * entry anywhere: it was not a zero, not a failure, not a warning — it was
   * absent from the response entirely, and a reader had no way to tell a Role
   * whose systems are all measured from one whose systems were never looked at.
   *
   * Every `RoleResource` the Role holds appears in exactly one of the two
   * lists, and `unmodelledSystems` carries no number by construction.
   *
   * 🚨 `RoleResource` IS THE WHOLE POPULATION OF BOTH LISTS, AND IT IS NOT
   * EVERYTHING THE ROLE HOLDS. A Role also holds systems through
   * `RoleCollectionGrant` and `RoleWorkspaceGrant` — the Roles index counts all
   * three tables under the word *systems* — and neither list can carry one. See
   * {@link GrantedRoleSystemsSchema}, which is where they are counted.
   *
   * This docblock claimed the wider completeness for both lists until
   * 2026-08-12. It was written when `RoleResource` was the only placement table
   * anyone read, and it became false when the index started counting the two
   * grant tables. Nothing typechecks a sentence, so it went on reading as a
   * guarantee.
   *
   * AN EMPTY ARRAY HERE MEANS "every `RoleResource` this Role holds has an
   * impact model", and it can only mean that, because the resource list is read
   * on the same request as the impacts. It never means "nobody looked".
   */
  unmodelledSystems: z.array(UnmodelledRoleSystemViewSchema).readonly(),
  /**
   * The systems this Role holds by GRANT rather than by placement, counted per
   * kind — the population `contributions` and `unmodelledSystems` cannot cover.
   *
   * ── WHY A THIRD FIELD RATHER THAN A WIDER `unmodelledSystems` ───────────────
   *
   * Folding grants into `unmodelledSystems` is the shorter edit and it is wrong
   * twice. `UnmodelledRoleSystemView.resourceType` is a `RoleResourceType`,
   * which `ROLE_OWNABLE_SYSTEM_NODE_TYPES` is proven equal to in BOTH
   * directions; widening it to admit `collection` and `workspace` would break
   * that proof and switch on every consumer keyed off "a Role can own this" —
   * including the board's `Remove from role` control, which deletes a
   * `RoleResource` row a grant does not have. And its `id` is documented as a
   * `RoleResource.id`, which a grant row has no way to supply.
   *
   * The deeper reason is that `unmodelledSystems` means "nobody has modelled
   * this YET". A grant is not awaiting a model: `RoleSystemImpact.roleResourceId`
   * is a `@unique` foreign key onto `RoleResource`, so no impact model can be
   * authored against a Collection or a Workspace grant at all. Listing one under
   * `NO_IMPACT_MODEL` would send a reader to author something the schema cannot
   * store.
   *
   * ── WHY COUNTS RATHER THAN IDENTITIES ───────────────────────────────────────
   *
   * The job of this field is to let a client name the population its aggregate
   * excludes — "2 collections and 1 workspace are held by grant and carry no
   * impact model" needs no identity. Identities would need a name read per kind,
   * and a Collection's human label is `displayName ?? name` (`name` is a
   * table-wide `@unique` machine identifier), which is a field a caller has
   * already drawn wrongly once on this feature. A count cannot draw the wrong
   * field.
   *
   * ── ZERO IS A MEASUREMENT ───────────────────────────────────────────────────
   *
   * Both counts come from reads issued on the same request as the coverage
   * figures, so `0` means the grant tables were asked and answered. It never
   * means "nobody looked" — the same distinction `unmodelledSystems` exists for.
   */
  grantedSystems: GrantedRoleSystemsSchema
});

/** Which bucket a system's figures land in, as the API returns it. */
export type RoleResourceLifecycleValue = z.infer<typeof RoleResourceLifecycleSchema>;

/** The numerator split by bucket, as the API returns it. */
export type ImpactPersonHoursByLifecycle = z.infer<
  typeof RoleCoverageViewSchema
>["impactPersonHoursByLifecycle"];

/** One money term of one system, as the API returns it. */
export type CoverageMoneyTermView = z.infer<typeof CoverageMoneyTermSchema>;

/** One role's money figures, as the API returns them. */
export type RoleCoverageMoneyView = z.infer<typeof RoleCoverageMoneySchema>;

/** Saved work re-expressed in money, as the API returns it. */
export type CoverageSavingsProjectionView = z.infer<typeof CoverageSavingsProjectionSchema>;

/** One system's contribution, as the API returns it. */
export type RoleCoverageContributionView = z.infer<typeof RoleCoverageContributionViewSchema>;

/** A system with no impact model, as the API returns it. */
export type UnmodelledRoleSystemView = z.infer<typeof UnmodelledRoleSystemViewSchema>;

/** How many systems a Role holds by grant, as the API returns them. */
export type GrantedRoleSystems = z.infer<typeof GrantedRoleSystemsSchema>;

/** Why a system produced no contribution. */
export type UnmodelledRoleSystemReason = z.infer<typeof UnmodelledRoleSystemReasonSchema>;

/** One role's coverage, as the API returns it. */
export type RoleCoverageView = z.infer<typeof RoleCoverageViewSchema>;
